/**
 * Desafios por posição do modo Treino (item 5.2). Cada um tem tempo, pontuação e medalhas de bronze, prata e
 * ouro; o melhor resultado fica salvo no navegador.
 * - Artilheiro, Tiro ao aro: gols em 60 s contra um goleiro difícil.
 * - Goleiro, Paredão: defesas em 10 arremessos.
 * - Batedor, Pontaria: adversários atordoados por Petardo em 60 s.
 * - Caçador, Caça à Faísca: capturas em 90 s.
 */
import { BotBrain } from "@besenliga/bots";
import { BALL, NET, PITCH, hoopCenters, type Position } from "@besenliga/shared";
import {
  quantizeInput,
  type AthleteState,
  type InputCommand,
  type PlayEvent,
  type Vec3,
  type WorldState,
} from "@besenliga/sim";
import { PLAYER_ID } from "../flight/ids";
import { calmWorld, type Marker, type Scenario, type ScenarioHud, type WorldAccess } from "./scenario";

export type ChallengeId = "tiro-ao-aro" | "paredao" | "pontaria" | "caca-faisca";
export type Medal = "ouro" | "prata" | "bronze" | null;

export interface ChallengeInfo {
  id: ChallengeId;
  role: Position;
  title: string;
  text: string;
  unit: string;
  seconds: number;
  /** Pontos para bronze, prata e ouro. */
  medals: readonly [number, number, number];
}

export const CHALLENGES: readonly ChallengeInfo[] = [
  {
    id: "tiro-ao-aro",
    role: "artilheiro",
    title: "Tiro ao aro",
    text: "Marque o máximo de gols em 60 s contra um goleiro difícil. A bola volta para você depois de cada gol ou erro.",
    unit: "gols",
    seconds: 60,
    medals: [4, 8, 12],
  },
  {
    id: "paredao",
    role: "goleiro",
    title: "Paredão",
    text: "Dez arremessos nos seus aros. Pegue a bola na área ou soque fora dela (clique perto da bola).",
    unit: "defesas",
    seconds: 50,
    medals: [4, 6, 8],
  },
  {
    id: "pontaria",
    role: "batedor",
    title: "Pontaria",
    text: "Quatro adversários voam em círculo. Rebata os Petardos neles: cada atordoado vale um ponto. Cuidado: os Petardos caçam você também.",
    unit: "acertos",
    seconds: 60,
    medals: [2, 4, 6],
  },
  {
    id: "caca-faisca",
    role: "cacador",
    title: "Caça à Faísca",
    text: "A Faísca aparece de novo depois de cada captura. Pegue quantas conseguir em 90 s.",
    unit: "capturas",
    seconds: 90,
    medals: [3, 6, 9],
  },
];

export function medalFor(info: ChallengeInfo, score: number): Medal {
  const [b, p, o] = info.medals;
  return score >= o ? "ouro" : score >= p ? "prata" : score >= b ? "bronze" : null;
}

export const MEDAL_LABEL: Record<Exclude<Medal, null>, string> = {
  ouro: "Ouro",
  prata: "Prata",
  bronze: "Bronze",
};

const ticks = (s: number) => Math.round(s * NET.simTickHz);
const me = (w: Readonly<WorldState>) => w.athletes.find((a) => a.id === PLAYER_ID);

function park(a: AthleteState, at: Vec3): void {
  a.position = { ...at };
  a.velocity = { x: 0, y: 0, z: 0 };
  a.stunTicks = 0;
}

/** Todos menos você (e quem o desafio usar) ficam parados longe da ação. */
function clearOthers(w: WorldState, keep: (a: AthleteState) => boolean): void {
  w.athletes.forEach((a, i) => {
    if (a.id === PLAYER_ID || keep(a)) return;
    park(a, { x: (i % 2 ? 1 : -1) * (10 + (i % 5) * 9), y: 6 + (i % 3) * 5, z: i % 4 < 2 ? -42 : 42 });
  });
}

/** Velocidade para a bola sair de `from` e chegar em `to` em `t` segundos, com a gravidade da Pelota. */
function ballistic(from: Vec3, to: Vec3, t: number): Vec3 {
  return {
    x: (to.x - from.x) / t,
    y: (to.y - from.y) / t + 0.5 * BALL.pelota.gravity * t,
    z: (to.z - from.z) / t,
  };
}

abstract class Challenge implements Scenario {
  score = 0;
  elapsed = 0;
  protected phaseTicks = 0;

  constructor(readonly info: ChallengeInfo) {}

  get finished(): boolean {
    return this.elapsed >= ticks(this.info.seconds) || this.extraDone();
  }

  protected extraDone(): boolean {
    return false;
  }

  abstract start(world: WorldAccess): void;
  protected abstract tickChallenge(world: WorldAccess, plays: readonly PlayEvent[]): void;

  inputs?(w: Readonly<WorldState>): Record<string, InputCommand>;

  afterStep(world: WorldAccess, plays: readonly PlayEvent[]): void {
    if (this.finished) return;
    this.elapsed += 1;
    this.phaseTicks += 1;
    this.tickChallenge(world, plays);
  }

  markers(w: Readonly<WorldState>): Marker[] {
    void w;
    return [];
  }

  hud(): ScenarioHud {
    const left = Math.max(0, this.info.seconds - this.elapsed / NET.simTickHz);
    const medal = medalFor(this.info, this.score);
    return {
      title: this.info.title,
      text: this.info.text,
      progress: `${this.score} ${this.info.unit}${this.progressExtra()}`,
      seconds: left,
      countdown: true,
      done: this.finished,
      score: this.score,
      summary: `${this.score} ${this.info.unit}${medal ? ` · medalha de ${MEDAL_LABEL[medal].toLowerCase()}` : " · sem medalha desta vez"}`,
    };
  }

  protected progressExtra(): string {
    return "";
  }
}

// ---------------------------------------------------------------- Tiro ao aro

const SHOT_SPOTS: readonly Vec3[] = [
  { x: 22, y: 16, z: 0 },
  { x: 26, y: 14, z: -14 },
  { x: 24, y: 18, z: 14 },
  { x: 20, y: 12, z: -6 },
  { x: 28, y: 16, z: 8 },
];

class TiroAoAro extends Challenge {
  private keeper = new BotBrain("", "dificil");
  private keeperId = "";
  private spot = 0;
  private waiting = 0;

  override start(world: WorldAccess): void {
    world.edit((w) => {
      calmWorld(w);
      const k = w.athletes.find((a) => a.team === "fora" && a.role === "goleiro");
      this.keeperId = k?.id ?? "";
      this.keeper = new BotBrain(this.keeperId, "dificil");
      clearOthers(w, (a) => a.id === this.keeperId);
      if (k) park(k, { x: PITCH.hoopX - 4, y: 16, z: 0 });
      this.give(w);
    });
  }

  private give(w: WorldState): void {
    this.phaseTicks = 0;
    const a = me(w);
    if (!a || !w.balls) return;
    park(a, SHOT_SPOTS[this.spot % SHOT_SPOTS.length] ?? { x: 22, y: 16, z: 0 });
    a.heading = { x: 1, z: 0 };
    this.spot += 1;
    w.balls.pelota.holder = a.id;
    w.balls.pelota.regrabTicks = 0;
  }

  override inputs(w: Readonly<WorldState>): Record<string, InputCommand> {
    return this.keeperId ? { [this.keeperId]: this.keeper.command(w) } : {};
  }

  protected override tickChallenge(world: WorldAccess, plays: readonly PlayEvent[]): void {
    if (plays.some((p) => p.kind === "gol" && p.team === "casa")) {
      this.score += 1;
      this.waiting = ticks(1);
    }
    const w = world.state;
    if (this.waiting > 0) {
      this.waiting -= 1;
      if (this.waiting === 0) world.edit((x) => this.give(x));
      return;
    }
    // Perdeu a bola (o goleiro pegou ou o arremesso errou): nova bola em 1,5 s.
    if (w.balls?.pelota.holder !== PLAYER_ID) {
      if (this.phaseTicks >= ticks(1.5)) world.edit((x) => this.give(x));
    } else this.phaseTicks = 0;
  }

  override markers(): Marker[] {
    return hoopCenters(1).map((h) => ({
      position: h,
      radius: PITCH.hoopRadius + 0.5,
      state: "proximo" as const,
    }));
  }
}

// ---------------------------------------------------------------- Paredão

const SHOTS = 10;
/** De onde sai cada arremesso e em qual aro (0 a 2) ele mira. */
const PAREDAO: readonly { from: Vec3; hoop: number; seconds: number }[] = [
  { from: { x: -30, y: 18, z: 0 }, hoop: 1, seconds: 1.3 },
  { from: { x: -32, y: 14, z: -12 }, hoop: 0, seconds: 1.3 },
  { from: { x: -30, y: 16, z: 12 }, hoop: 2, seconds: 1.2 },
  { from: { x: -28, y: 20, z: -4 }, hoop: 2, seconds: 1.1 },
  { from: { x: -34, y: 12, z: 6 }, hoop: 0, seconds: 1.3 },
  { from: { x: -30, y: 18, z: 0 }, hoop: 0, seconds: 1.0 },
  { from: { x: -26, y: 15, z: 14 }, hoop: 1, seconds: 1.0 },
  { from: { x: -32, y: 22, z: -14 }, hoop: 2, seconds: 1.2 },
  { from: { x: -28, y: 16, z: 2 }, hoop: 1, seconds: 0.9 },
  { from: { x: -30, y: 14, z: -8 }, hoop: 0, seconds: 0.9 },
];

class Paredao extends Challenge {
  shot = 0;
  private live = false;
  private resolved = false;

  override start(world: WorldAccess): void {
    world.edit((w) => {
      calmWorld(w);
      clearOthers(w, () => false);
      const a = me(w);
      if (a) {
        park(a, { x: -PITCH.hoopX + 4, y: 16, z: 0 });
        a.heading = { x: 1, z: 0 };
      }
      if (w.balls) {
        w.balls.pelota.holder = null;
        w.balls.pelota.position = { x: -30, y: 2, z: 30 };
      }
    });
    this.phaseTicks = 0;
  }

  protected override extraDone(): boolean {
    return this.shot >= SHOTS && !this.live;
  }

  protected override progressExtra(): string {
    return ` de ${Math.min(this.shot, SHOTS)}`;
  }

  private fire(world: WorldAccess): void {
    const s = PAREDAO[this.shot % PAREDAO.length];
    if (!s) return;
    const target = hoopCenters(-1)[s.hoop];
    if (!target) return;
    world.edit((w) => {
      if (!w.balls) return;
      w.balls.pelota.holder = null;
      w.balls.pelota.position = { ...s.from };
      w.balls.pelota.velocity = ballistic(s.from, target, s.seconds);
      w.balls.pelota.lastTouch = null;
      w.balls.pelota.lastTeam = "fora";
      w.balls.pelota.regrabTicks = 0;
    });
    this.shot += 1;
    this.live = true;
    this.resolved = false;
    this.phaseTicks = 0;
  }

  protected override tickChallenge(world: WorldAccess, plays: readonly PlayEvent[]): void {
    if (this.live) {
      if (
        !this.resolved &&
        plays.some((p) => (p.kind === "posse" || p.kind === "defesa") && p.athlete === PLAYER_ID)
      ) {
        this.score += 1;
        this.resolved = true;
      }
      if (plays.some((p) => p.kind === "gol")) this.resolved = true;
      // Cada arremesso dura até 3 s; depois vem o próximo.
      if (this.phaseTicks >= ticks(3)) this.live = false;
      return;
    }
    if (this.shot < SHOTS && this.phaseTicks >= ticks(1)) this.fire(world);
  }

  override markers(): Marker[] {
    if (this.live || this.shot >= SHOTS) return [];
    const s = PAREDAO[this.shot % PAREDAO.length];
    return s ? [{ position: s.from, radius: 1.5, state: "proximo" }] : [];
  }
}

// ---------------------------------------------------------------- Pontaria

class Pontaria extends Challenge {
  private targets: string[] = [];

  override start(world: WorldAccess): void {
    world.edit((w) => {
      calmWorld(w);
      this.targets = w.athletes
        .filter((a) => a.team === "fora" && a.role !== "goleiro")
        .slice(0, 4)
        .map((a) => a.id);
      clearOthers(w, (a) => this.targets.includes(a.id));
      this.targets.forEach((id, i) => {
        const a = w.athletes.find((x) => x.id === id);
        const ang = (i / 4) * Math.PI * 2;
        if (a) park(a, { x: 10 + Math.cos(ang) * 18, y: 12 + i * 2, z: Math.sin(ang) * 18 });
      });
      const a = me(w);
      if (a) park(a, { x: -18, y: 13, z: 0 });
      if (w.balls) {
        w.balls.petardos.forEach((p, i) => {
          p.position = { x: -14, y: 13, z: i === 0 ? -6 : 6 };
          p.velocity = { x: 0, y: 0, z: 0 };
          p.mode = "descanso";
          p.modeTicks = ticks(2);
        });
        w.balls.pelota.position = { x: 0, y: 2, z: 44 };
      }
    });
  }

  /** Os alvos voam num círculo de 18 m em volta de (10, 12, 0), sem parar. */
  override inputs(w: Readonly<WorldState>): Record<string, InputCommand> {
    const out: Record<string, InputCommand> = {};
    for (const id of this.targets) {
      const a = w.athletes.find((x) => x.id === id);
      if (!a) continue;
      const rx = a.position.x - 10;
      const rz = a.position.z;
      const r = Math.sqrt(rx * rx + rz * rz) || 1;
      // Tangente mais uma correção para o raio.
      const tx = -rz / r + (rx / r) * ((18 - r) / 10);
      const tz = rx / r + (rz / r) * ((18 - r) / 10);
      out[id] = quantizeInput(tx * 0.55, 0, tz * 0.55, 0);
    }
    return out;
  }

  protected override tickChallenge(_world: WorldAccess, plays: readonly PlayEvent[]): void {
    for (const p of plays)
      if (p.kind === "atordoado" && p.other === PLAYER_ID && this.targets.includes(p.athlete))
        this.score += 1;
  }

  override markers(w: Readonly<WorldState>): Marker[] {
    return this.targets.flatMap((id) => {
      const a = w.athletes.find((x) => x.id === id);
      return a && a.stunTicks === 0
        ? [
            {
              position: { x: a.position.x, y: a.position.y + 3.2, z: a.position.z },
              radius: 1.3,
              state: "proximo" as const,
            },
          ]
        : [];
    });
  }
}

// ---------------------------------------------------------------- Caça à Faísca

const SPARK_SPOTS: readonly Vec3[] = [
  { x: 10, y: 20, z: 8 },
  { x: -30, y: 14, z: -18 },
  { x: 35, y: 26, z: -10 },
  { x: -10, y: 10, z: 24 },
  { x: 20, y: 18, z: -26 },
];

class CacaFaisca extends Challenge {
  private next = 0;
  private respawn = 0;

  override start(world: WorldAccess): void {
    world.edit((w) => {
      calmWorld(w);
      clearOthers(w, () => false);
      const a = me(w);
      if (a) park(a, { x: -20, y: 22, z: 0 });
      if (w.balls) w.balls.pelota.position = { x: 0, y: 2, z: 44 };
      this.spawn(w);
    });
  }

  private spawn(w: WorldState): void {
    const f = w.balls?.faisca;
    if (!f) return;
    const at = SPARK_SPOTS[this.next % SPARK_SPOTS.length] ?? { x: 0, y: 20, z: 0 };
    this.next += 1;
    f.active = true;
    f.captured = false;
    f.capturedBy = null;
    f.position = { ...at };
    f.velocity = { x: 0, y: 0, z: 0 };
    f.waypoint = { ...at };
    f.wanderTicks = 0;
    f.activeTicks = 0;
  }

  protected override tickChallenge(world: WorldAccess, plays: readonly PlayEvent[]): void {
    if (plays.some((p) => p.kind === "captura" && p.athlete === PLAYER_ID)) {
      this.score += 1;
      this.respawn = ticks(1.5);
    }
    if (this.respawn > 0) {
      this.respawn -= 1;
      if (this.respawn === 0) world.edit((w) => this.spawn(w));
    }
  }
}

export function createChallenge(id: ChallengeId): Challenge {
  const info = CHALLENGES.find((c) => c.id === id);
  if (!info) throw new Error(`Desafio ${id} não existe`);
  switch (id) {
    case "tiro-ao-aro":
      return new TiroAoAro(info);
    case "paredao":
      return new Paredao(info);
    case "pontaria":
      return new Pontaria(info);
    case "caca-faisca":
      return new CacaFaisca(info);
  }
}

export type { Challenge };

// ---------------------------------------------------------------- recordes

export const RECORDS_KEY = "besenliga.desafios.v1";
type Store = Pick<Storage, "getItem" | "setItem">;

export function loadRecords(store: Store | null): Partial<Record<ChallengeId, number>> {
  try {
    const raw = store?.getItem(RECORDS_KEY);
    const v: unknown = raw ? JSON.parse(raw) : {};
    if (typeof v !== "object" || v === null) return {};
    const out: Partial<Record<ChallengeId, number>> = {};
    for (const c of CHALLENGES) {
      const n = (v as Record<string, unknown>)[c.id];
      if (typeof n === "number" && Number.isFinite(n) && n >= 0) out[c.id] = n;
    }
    return out;
  } catch {
    return {};
  }
}

/** Guarda o resultado se for o melhor; devolve se foi recorde (zero ponto fica guardado, mas não é recorde). */
export function saveRecord(store: Store | null, id: ChallengeId, score: number): boolean {
  const all = loadRecords(store);
  if ((all[id] ?? -1) >= score) return false;
  const first = all[id] === undefined;
  all[id] = score;
  try {
    store?.setItem(RECORDS_KEY, JSON.stringify(all));
  } catch {
    return false;
  }
  return score > 0 || !first;
}
